import { useState, useEffect } from 'react';

import { AppIcon, AppIcons } from '@backrs_ui/common-ui/src/designSystem/icons/AppIcon';

import CheckIcon from '@mui/icons-material/Check';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import {
  Autocomplete,
  Chip,
  TextField,
  Theme,
  SxProps,
  FilterOptionsState,
  Box,
  Checkbox,
} from '@mui/material';
import { ControllerRenderProps, FieldError, FieldValues } from 'react-hook-form';

import { LeftAlignedLabelWrapper } from './LeftAlignedLabelWrapper';
import { BackrsFormField } from './types';

const determineIsOptionEqualToValue = (
  option: OptionType,
  value: OptionType | OptionType[],
  field: BackrsFormField,
) => {
  if (Array.isArray(value) && field.autoCompleteEndpoint) {
    return value.some((v) => v.value === option.value);
  }

  if (Array.isArray(value)) {
    return value.some((v) => v.value === option.value);
  }

  // value is selection, option is list of possibilities
  if (option.value === value.value) {
    return true;
  }

  return false;
};

const handleFilterOptions = (
  options: OptionType[],
  state: FilterOptionsState<OptionType>,
  field: BackrsFormField,
) => {
  if (field.manualFilterOptions) {
    if (field.onInputChange) {
      return options;
    }

    return options.filter((option) =>
      option.label.toLowerCase().includes(state.inputValue.toLowerCase()),
    );
  } else if (state.inputValue.length < 4 && field.autoCompleteEndpoint) {
    return [];
  }

  return options;
};

type OptionType = Readonly<{
  label: string;
  value: string;
  icon?: AppIcons;
}>;

type BackrsChipProps = Readonly<{
  option: OptionType;
  onDelete?: (event?: React.MouseEvent<HTMLDivElement>) => void;
  groupKey: string;
  fieldName: string;
  index: number;
  chipLayout: 'underneath' | 'inline';
}>;

const BackrsChip = ({
  option,
  onDelete,
  groupKey,
  fieldName,
  index,
  chipLayout,
}: BackrsChipProps) => {
  return (
    <Chip
      variant="tag"
      label={option.label.length > 20 ? `${option.label.slice(0, 10)}...` : option.label}
      type="trailing"
      size="md"
      sx={chipSx(!!option.icon || false, chipLayout)}
      icon={
        option.icon ? (
          <Box sx={iconContainerStyle}>
            <AppIcon
              iconName={option.icon}
              diameter="12px"
              color="Colors/Foreground/fg-quaternary"
            />
          </Box>
        ) : undefined
      }
      deleteIcon={
        <Box sx={deleteIconContainerStyle}>
          <AppIcon
            iconName="XClose"
            diameter="12px"
            color={option.icon ? 'Colors/Foreground/fg-quaternary' : 'Blue/400'}
          />
        </Box>
      }
      key={`${groupKey}-${fieldName}-${option.value}-${index}`}
      onDelete={onDelete}
    />
  );
};

type BackrsFormAutoCompleteProps = Readonly<{
  isMultiple: boolean;
  autoCompleteOptions: OptionType[];
  options: OptionType[];
  field: BackrsFormField;
  controllerField: ControllerRenderProps<FieldValues, string>;
  groupedFields: Record<string, BackrsFormField[]>;
  index: number;
  error: FieldError | undefined;
  groupKey: string;
  isDisabled?: boolean;
}>;

export const BackrsFormAutoComplete = ({
  isMultiple,
  autoCompleteOptions,
  field,
  options,
  controllerField,
  groupedFields,
  index,
  error,
  groupKey,
  isDisabled,
}: BackrsFormAutoCompleteProps) => {
  const [inputValue, setInputValue] = useState('');
  const [localOptions, setLocalOptions] = useState<OptionType[]>(autoCompleteOptions);

  const sourceOptions = field.autoCompleteEndpoint ? localOptions : options;

  // Create option objects for values in the form
  const valueOptionsFromForm =
    isMultiple && Array.isArray(controllerField.value)
      ? controllerField.value.map((value) => {
          // First try to find in source options
          const existingOption = sourceOptions.find((opt) => opt.value === value);

          if (existingOption) return existingOption;

          // If not found, create a new option
          return { label: value, value };
        })
      : [];

  // Combine with source options if not found
  const selectedOptions = isMultiple
    ? Array.isArray(controllerField.value)
      ? valueOptionsFromForm
      : []
    : sourceOptions.find((option) => option.value === controllerField.value) || null;

  // Update local options when form values or autocomplete options change
  useEffect(() => {
    // Add any form values not in options to the options list
    if (isMultiple && Array.isArray(controllerField.value) && controllerField.value.length > 0) {
      const newOptions = [...autoCompleteOptions];

      controllerField.value.forEach((value) => {
        // Check if this value is already in options
        const exists = newOptions.some((option) => option.value === value);

        if (!exists) {
          newOptions.push({ label: value, value });
        }
      });

      setLocalOptions(newOptions);
    } else {
      setLocalOptions(autoCompleteOptions);
    }
  }, [autoCompleteOptions, controllerField.value, isMultiple]);

  const chipLayout = field.selectOptions?.chipLayout || 'inline';
  const placeholderValue =
    chipLayout === 'underneath' || !Array.isArray(selectedOptions) || selectedOptions.length === 0
      ? field.placeholder
      : '';

  const autoComplete = (
    <>
      <Autocomplete<OptionType, boolean, boolean, boolean>
        multiple={isMultiple}
        disabled={field.disabled || isDisabled}
        options={field.manualFilterOptions ? sourceOptions : localOptions}
        popupIcon={
          <AppIcon iconName="ChevronDown" diameter="20px" color="Colors/Foreground/fg-quaternary" />
        }
        isOptionEqualToValue={(option, value) =>
          determineIsOptionEqualToValue(option as OptionType, value, field)
        }
        filterOptions={(options, state) =>
          handleFilterOptions(options as OptionType[], state, field)
        }
        noOptionsText={field.autoCompleteEndpoint ? 'Please enter at least 4 characters' : ''}
        {...(!field.autoCompleteEndpoint && {
          value: selectedOptions,
        })}
        {...(field.autoCompleteEndpoint &&
          isMultiple && {
            inputValue,
            defaultValue: isMultiple ? valueOptionsFromForm : null,
          })}
        {...(field.autoCompleteEndpoint &&
          !isMultiple && {
            value: selectedOptions,
          })}
        onInputChange={(_, newInputValue) => {
          setInputValue(newInputValue);
          field.onInputChange?.(newInputValue, field.name || '');
        }}
        onChange={(_, newValue) => {
          controllerField.onChange(
            Array.isArray(newValue)
              ? newValue.map((option) => (typeof option === 'string' ? option : option.value))
              : (typeof newValue === 'string' ? newValue : newValue?.value) || '',
          );

          // Force update local state to match the new value
          if (isMultiple) {
            const newOptions = Array.isArray(newValue)
              ? newValue.map((option) =>
                  typeof option === 'string'
                    ? { label: option, value: option }
                    : (option as OptionType),
                )
              : [];

            setLocalOptions((prev) => {
              // Add any newly selected options
              const updatedOptions = [...prev];

              newOptions.forEach((option) => {
                if (!updatedOptions.some((o) => o.value === option.value)) {
                  updatedOptions.push(option);
                }
              });

              return updatedOptions;
            });
          }

          field.afterOnChange?.(
            newValue as { label: string; value: string; variant?: string; title?: string } | null,
          );
        }}
        sx={autoCompleteSx(
          (Array.isArray(selectedOptions) ? selectedOptions : [])
            .map((option) => option.label.length)
            .reduce((a, b) => a + b, 0) || 0,
          chipLayout,
        )}
        clearIcon={
          chipLayout === 'underneath' ? null : (
            <AppIcon iconName="XClose" diameter="16px" color="Gray/500" />
          )
        }
        /**
         * FOR MULTIPLE AUTO COMPLETES, WE RENDER CHIPS
         */
        {...((isMultiple === false && field.autoCompleteEndpoint) || field.manualFilterOptions
          ? { freeSolo: true }
          : {})}
        renderOption={(props, option, { selected }) => {
          const customProps = {
            ...props,
            style: {
              ...props.style,
              backgroundColor: 'transparent',
            },
          };

          const optionCustomClickAction = field.selectOptions?.options?.find(
            (opt) => opt.value === option.value,
          )?.onClick;

          return (
            <li
              {...customProps}
              key={`${groupKey}-${field.name}-${option.value}-${index}`}
              {...(!!optionCustomClickAction && {
                onClick: () => optionCustomClickAction(),
              })}
            >
              {isMultiple && (
                <Checkbox
                  icon={<CheckBoxOutlineBlankIcon />}
                  checkedIcon={<CheckIcon />}
                  style={{ marginRight: 8 }}
                  checked={selected}
                  disableRipple
                />
              )}
              {option.label}
            </li>
          );
        }}
        renderTags={
          chipLayout === 'underneath'
            ? () => null
            : (tagValue, getTagProps) =>
                tagValue.map((option, index: number) => {
                  const { onDelete } = getTagProps({ index });
                  const optionObj = option as OptionType;

                  return (
                    <BackrsChip
                      key={`${groupKey}-${field.name}-${optionObj.value}-${index}`}
                      chipLayout={chipLayout}
                      option={optionObj}
                      onDelete={(event) => {
                        // Call the original onDelete from getTagProps
                        if (onDelete) {
                          onDelete(event);
                        }

                        // As a fallback, also handle directly
                        const currentValues = Array.isArray(controllerField.value)
                          ? controllerField.value
                          : [];

                        const updatedValue = currentValues.filter(
                          (v: string) => v !== optionObj.value,
                        );

                        controllerField.onChange(updatedValue);
                      }}
                      groupKey={groupKey}
                      fieldName={field.name || ''}
                      index={index}
                    />
                  );
                })
        }
        /**
         * THE VISUAL FOR THE AUTO COMPLETE IS A TEXT FIELD
         */
        renderInput={(params) => (
          <TextField
            {...params}
            id={field.name}
            name={field.name}
            label={field.labelPlacement === 'left' ? undefined : field.label}
            error={!!error?.message}
            helperText={error?.message}
            disabled={field.disabled || isDisabled}
            placeholder={placeholderValue}
            inputProps={{
              ...params.inputProps,
              sx: [params.inputProps?.style, textFieldInputParamsSx].filter(Boolean),
            }}
            InputProps={{
              ...params.InputProps,
              ...(field.customStartAdornment && {
                startAdornment: field.customStartAdornment,
              }),
            }}
            sx={selectTextFieldStyle(field.divider || false)}
          />
        )}
        disableCloseOnSelect={isMultiple}
      />
      {chipLayout === 'underneath' &&
        isMultiple &&
        Array.isArray(selectedOptions) &&
        selectedOptions.length > 0 && (
          <Box sx={underneathTagContainerSx}>
            {selectedOptions.map((option, index) => {
              const optionObj = option as OptionType;

              return (
                <BackrsChip
                  key={`${groupKey}-${field.name}-${optionObj.value}-${index}`}
                  chipLayout={chipLayout}
                  option={optionObj}
                  onDelete={() => {
                    const currentValues = Array.isArray(controllerField.value)
                      ? controllerField.value
                      : [];
                    const updatedValue = currentValues.filter((v: string) => v !== optionObj.value);

                    controllerField.onChange(updatedValue);
                  }}
                  groupKey={groupKey}
                  fieldName={field.name || ''}
                  index={index}
                />
              );
            })}
          </Box>
        )}
    </>
  );

  if (field.labelPlacement === 'left') {
    return (
      <LeftAlignedLabelWrapper
        field={field}
        error={!!error}
        isDisabled={field.disabled || isDisabled || false}
        groupedFields={groupedFields}
        index={index}
      >
        {autoComplete}
      </LeftAlignedLabelWrapper>
    );
  }

  return autoComplete;
};

const underneathTagContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: 1,
  mb: backrs.spacing['spacing-lg'],
});

const selectTextFieldStyle =
  (divider: boolean): SxProps<Theme> =>
  ({ backrs }) => ({
    mb: backrs.spacing['spacing-none'],

    ...(divider && {
      mb: backrs.spacing['spacing-none'],
    }),

    width: '100%',

    '& .MuiAutocomplete-inputRoot': {
      paddingY: `${backrs.spacing['spacing-xs']}`,
      paddingX: `${backrs.spacing['spacing-md']}`,
      gap: `${backrs.spacing['spacing-none']}`,
      height: '100%',
    },

    '& .MuiAutocomplete-input': {
      textOverflow: 'ellipsis',
      overflow: 'hidden',
      whiteSpace: 'nowrap',
      paddingRight: `${backrs.spacing['spacing-8xl']}`,
    },
  });

const chipSx =
  (icon: boolean, chipLayout: 'underneath' | 'inline'): SxProps<Theme> =>
  ({ backrs }) => ({
    backgroundColor: backrs.colors['Blue/50'],
    border: `1px solid ${backrs.colors['Blue/200']}`,
    color: backrs.colors['Blue/700'],
    ...(icon && {
      backgroundColor: backrs.colors['Colors/Base/White'],
      border: `1px solid ${backrs.colors['Colors/Border/border-primary']}`,
      color: backrs.colors['Colors/Text/text-secondary'],
    }),
    marginRight: chipLayout === 'underneath' ? 'inherit' : backrs.spacing['spacing-sm'],
    ...(chipLayout === 'inline' && {
      marginTop: backrs.spacing['spacing-xs'],
      marginBottom: backrs.spacing['spacing-xs'],
    }),
    '& .MuiChip-icon': {
      marginRight: backrs.spacing['spacing-none'],
      marginLeft: backrs.spacing['spacing-none'],
    },
  });

const iconContainerStyle: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  backgroundColor: backrs.colors['Colors/Background/bg-secondary-hover'],
  width: '16px',
  height: '16px',
  borderRadius: '50%',
  border: `0.5px solid ${backrs.colors['Colors/Base/Black']}14`,
});

const deleteIconContainerStyle: SxProps<Theme> = ({ backrs }) => ({
  cursor: 'pointer',
  margin: `${backrs.spacing['spacing-none']} !important`,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
});

const autoCompleteSx =
  (selectedItemsCharacterCount: number, chipLayout: 'underneath' | 'inline'): SxProps<Theme> =>
  ({ backrs }) => ({
    cursor: 'pointer',
    width: '100%',
    padding: `${backrs.spacing['spacing-none']}`,
    '& .MuiAutocomplete-root': {
      paddingY: `${backrs.spacing['spacing-none']} !important`,
    },
    '& .MuiAutocomplete-inputRoot': {
      paddingY: `${backrs.spacing[selectedItemsCharacterCount > 40 && chipLayout !== 'underneath' ? 'spacing-md' : 'spacing-none']} !important`,
      height: 'unset',
      border: `1px solid ${backrs.colors['Colors/Border/border-primary']}`,
      ...(chipLayout === 'inline' && {
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'flex-start',
        height: 'auto !important',
        minHeight: '40px',
      }),
    },

    '& .MuiAutocomplete-endAdornment': {
      top: selectedItemsCharacterCount > 40 && chipLayout !== 'underneath' ? '16px' : '24px',
    },
    '& .MuiAutocomplete-tag': {
      margin: `${backrs.spacing['spacing-xxs']}`,
    },
  });

const textFieldInputParamsSx: SxProps<Theme> = ({ backrs }) => ({
  textOverflow: 'ellipsis',
  paddingRight: backrs.spacing['spacing-6xl'],
  overflow: 'hidden',
  whiteSpace: 'nowrap',
});
